import React from 'react';
// Components
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import { WpCollapsibleGroup, WpCollapsibleGroupItem } from 'Wpds/components/collapsible-group';
import { WpAlert } from 'Wpds/components/alert';
import WpLink from 'Wpds/components/link/WpLink';
import { WpIcon } from 'Wpds/components/icon';
import { WpText } from 'Wpds/components/text';

// Constants
import ExternalLinksConstants from 'Shared/constants/ExternalLinks.constants';

// Style
import './ProductiveRelationshipSteps.scss';

const ProductiveRelationshipSteps = () => {
  const baseClassName = 'ProductiveRelationshipSteps';

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__title`}>
        <WpText tag="p" variant="bodyXl" bold>
          Steps to set up a productive relationship
        </WpText>
      </div>
      <WpContainer>
        <WpContainerBody>
          <p>
            Working with someone similar to yourself is intuitive. But, when the person you’re working with is different
            than you, it takes deliberate effort to change your own behavior so that you interact with them effectively.
            To set up a productive relationship, follow these four steps.
          </p>
          <WpCollapsibleGroup multiOpen={false} numbered defaultOpenIds={['one']}>
            <WpCollapsibleGroupItem
              title="Foster self-awareness, and awareness of the other person."
              collapsibleId="one"
            >
              Conduct a thoughtful review of each person’s strengths and potential pitfalls. Then, provide each person
              with the other’s profile of strengths and potential caution areas. This type of transparency and mutual
              understanding establishes a foundation of awareness and builds trust.
            </WpCollapsibleGroupItem>
            <WpCollapsibleGroupItem title="Identify competing styles." collapsibleId="two">
              <p>
                Look for areas where the two parties are significantly different from one another in terms of their
                behavioral preferences and natural strengths.{' '}
              </p>
              <p>
                For example, if one co-worker is detail-oriented while the other is spontaneous, it’s important to
                surface this competing style. Otherwise, if each acts solely in line with their nature, the
                interpersonal dynamic may become strained. It’s important to point out to both employees that neither
                style is right or wrong, and neither is good or bad. They’re simply different and must be equally
                understood and respected.
              </p>
            </WpCollapsibleGroupItem>
            <WpCollapsibleGroupItem title="Highlight competing goals." collapsibleId="three">
              Many times, two people will be pursuing respective objectives that compete with one another in some way. A
              common example in organizations are the competing goals of innovation and adaptation vs. stability and
              control. If one party is working to discover new ways of doing things while the other party is working to
              maintain high levels of quality, efficiency, and predictability, then these objectives compete with one
              another and the natural friction may spill over into the interpersonal dynamic.
            </WpCollapsibleGroupItem>
            <WpCollapsibleGroupItem title="Pre-negotiate optimal ways of working together." collapsibleId="four">
              <p>
                The best time to sow the seeds of a productive relationship is when it is a new relationship. Before the
                partnering work begins, execute the above steps. After creating a thorough understanding of respective
                strengths and any competing styles or goals, the parties can discuss how they will conduct their work
                together.
              </p>
              <p>
                For example, one party may be extremely extroverted and prefer to “think aloud” while the other is more
                introverted and prefers time in quiet isolation to generate and process ideas. Under this circumstance,
                how can you help the two parties come to some agreement about how they will interact before the real
                work begins? By coming to a mutual agreement, the pair will be able to refer back to it when pressure
                inevitably builds, deadlines loom, and individuals tend to revert to their natural behavioral styles.
              </p>
            </WpCollapsibleGroupItem>
          </WpCollapsibleGroup>
          <div className={`${baseClassName}__alert`}>
            <WpAlert type="info" showIcon={true}>
              Having difficulty working through a relationship? Learn more about how to &nbsp;
              <WpLink href={ExternalLinksConstants.relationship_resolve_conflict}>
                <WpIcon name="external-link" />
                Resolve a conflict
              </WpLink>
            </WpAlert>
          </div>
        </WpContainerBody>
      </WpContainer>
    </div>
  );
};

export default ProductiveRelationshipSteps;
